import { makeAutoObservable } from 'mobx'
import { truthy } from 'shared/lib'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import {
  type IBaseSmartCallFlowData,
  type IFullSmartCallFlowData,
  type ISmartCallFlowStep,
} from 'entities/SmartCall/api/types'
import { type Inbox } from 'entities/Inbox/model/Inbox'
import type { IFlowAction } from 'widgets/Flow'
import { RingStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/Ring'
import { getSmartActionNextIdsFromStep } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/utils/getSmartActionNextIdsFromStep'
import { getSmartCallActionTypeFromStep } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/utils/getSmartCallActionTypeFromStep'
import { ForwardCallStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/ForwardCall'
import {
  CallEndsStore,
  CallQueueStore,
  IncomingCallStore,
  PlayAudioStore,
  SendToVoicemailStore,
} from '../ui/actions'
import { type ISmartCallFlowAction, type ISmartCallFlowActionType } from '../ui/actions/type'

export class SmartCallFlowBuilderStore {
  constructor(
    public readonly entities: IEntitiesStore,
    readonly parent: { inbox: Inbox | null },
    public response?: IFullSmartCallFlowData
  ) {
    this._init(this.response)
    makeAutoObservable(this)
  }

  get params(): IBaseSmartCallFlowData | null {
    if (!this.trigger) return null
    return {
      name: this.name,
      description: '',
      definition: {
        trigger: this.trigger.step,
        steps: this.steps,
      },
    }
  }

  name = ''
  template: string | null = null

  get trigger() {
    const trigger = this.actions[0]
    if (trigger instanceof IncomingCallStore) {
      return trigger
    }
  }

  get steps(): IBaseSmartCallFlowData['definition']['steps'] {
    const steps: IBaseSmartCallFlowData['definition']['steps'] = {}
    this.actions.slice(1).forEach((action) => {
      steps[action.actionStore.id] = action.step
    })

    return steps
  }

  private _actionsMap: Map<string, ISmartCallFlowAction> = new Map()
  addAction = (action: ISmartCallFlowAction) => {
    this._actionsMap.set(action.actionStore.id, action)
  }

  get actions() {
    return Array.from(this._actionsMap.values())
  }

  getActionById = (id: string) => {
    return this._actionsMap.get(id)
  }

  deleteAction = (workFlowAction: IFlowAction, isNextDelete: boolean) => {
    const action = workFlowAction.actionStore
    if (action.id === this.selectedActionStore?.actionStore.id) {
      this.closeSelectedAction()
    }

    const parent = this.getActionById(action.parentId)?.actionStore
    const children = action.nextIds
      .map(this.getActionById)
      .map((action) => action?.actionStore)
      .filter(truthy)

    if (!parent) return
    if (isNextDelete) {
      const end = new CallEndsStore({ parentId: parent.id })
      parent.replaceNextId(action.id, end.actionStore.id)
      this._actionsMap.set(end.actionStore.id, end)
      this._removeActionAndAllChildren(workFlowAction)
    } else {
      parent.removeNextId(action.id)
      children.forEach((child) => {
        parent.addNextId(child.id)
        child.setParentId(parent.id)
      })
      this._actionsMap.delete(action.id)
    }
  }

  createAction = (
    actionType: ISmartCallFlowActionType,
    {
      parentId,
      id,
      childId,
      step,
    }: {
      parentId: string
      childId?: string
      id?: string
      step?: ISmartCallFlowStep
    }
  ) => {
    const commonProps = { parentId, id, childId }

    switch (actionType) {
      case 'incomingCall': {
        return new IncomingCallStore()
      }
      case 'call_queue': {
        const args = (step?.id === 'call_queue' && step.args) || undefined
        return new CallQueueStore(this.entities, { ...commonProps, args }, this.parent)
      }
      case 'ring': {
        const args = (step?.id === 'calling_flow' && step.args) || undefined
        return new RingStore(this.entities, { ...commonProps, args })
      }
      case 'sendToVoicemail': {
        return new SendToVoicemailStore(this.entities, { ...commonProps }, this.parent)
      }
      case 'playAudio': {
        return new PlayAudioStore(this.entities, { ...commonProps }, this.parent)
      }
      case 'forwardCall': {
        const args = (step?.id === 'forward_call_multi' && step.args) || undefined
        return new ForwardCallStore(this.entities, { ...commonProps, args }, this.parent)
      }
      case 'callEnds': {
        return new CallEndsStore({ ...commonProps })
      }
      default: {
        return null
      }
    }
  }

  addActionByType = (actionType: ISmartCallFlowActionType, source: string, target: string) => {
    const sourceAction = this.getActionById(source)?.actionStore
    const targetAction = this.getActionById(target)?.actionStore

    const newWorkflowAction: ISmartCallFlowAction | null = this.createAction(actionType, {
      parentId: source,
      childId: target,
    })

    if (newWorkflowAction) {
      const isNeedCreateSendToVoicemailStore =
        newWorkflowAction instanceof RingStore &&
        !this.actions.find((action) => action instanceof SendToVoicemailStore)

      if (isNeedCreateSendToVoicemailStore) {
        sourceAction?.replaceNextId(target, newWorkflowAction.actionStore.id)
        this.addAction(newWorkflowAction)
        this.setSelectedActionStore(newWorkflowAction)

        const sendToVoicemailAction: ISmartCallFlowAction | null = this.createAction(
          'sendToVoicemail',
          {
            parentId: newWorkflowAction.actionStore.id,
            childId: target,
          }
        )
        if (sendToVoicemailAction) {
          newWorkflowAction.actionStore?.replaceNextId(target, sendToVoicemailAction.actionStore.id)
          targetAction?.setParentId(sendToVoicemailAction.actionStore.id)
          this.addAction(sendToVoicemailAction)
        }
      } else {
        sourceAction?.replaceNextId(target, newWorkflowAction.actionStore.id)
        targetAction?.setParentId(newWorkflowAction.actionStore.id)

        this.addAction(newWorkflowAction)
        this.setSelectedActionStore(newWorkflowAction)
      }
    }
  }

  private _selectedActionStore: ISmartCallFlowAction | null = null
  get selectedActionStore() {
    return this._selectedActionStore
  }
  setSelectedActionStore = (selectedActionStore: ISmartCallFlowAction | null) => {
    if (selectedActionStore?.actionStore.id === this._selectedActionStore?.actionStore.id) return
    this._selectedActionStore = selectedActionStore
  }
  closeSelectedAction = () => {
    this._selectedActionStore = null
  }

  private _removeActionAndAllChildren = (action: IFlowAction) => {
    this._actionsMap.delete(action.actionStore.id)
    action.actionStore.nextIds.forEach((id: string) => {
      const actionNew = this._actionsMap.get(id)
      if (actionNew) {
        return this._removeActionAndAllChildren(actionNew)
      }
    })
  }

  get disabled() {
    return false
  }

  validateForPublish = (): boolean => {
    return this.actions
      .map((action) => ('validate' in action ? action.validate() : true))
      .every(Boolean)
  }

  setError = (error: Record<string, string>) => {
    Object.entries(error).forEach(([key, value]) => {
      const id = key.split('.')[0]
      if (id) {
        const action = this.getActionById(id)
        if (action && 'setApiError' in action) {
          action?.setApiError(key, value)
        }
      }
    })
  }

  private _init = (response?: IFullSmartCallFlowData) => {
    if (response) {
      const definition = response?.definition

      const trigger = new IncomingCallStore()
      this.addAction(trigger)

      const firstStepId = response.definition.trigger.next

      const createAction = (newStepId: string, parentWorkflowAction: ISmartCallFlowAction) => {
        const newStep = definition.steps[newStepId]
        if (newStep) {
          const nextIds: Array<string | undefined> = getSmartActionNextIdsFromStep(newStep)
          const actionType = getSmartCallActionTypeFromStep(newStep)

          if (!actionType) {
            return
          }

          const newWorkflowAction = this.createAction(actionType, {
            id: newStepId,
            parentId: parentWorkflowAction.actionStore.id,
            step: newStep,
          })
          if (newWorkflowAction) {
            parentWorkflowAction.actionStore.addNextId(newWorkflowAction.actionStore.id)
            this.addAction(newWorkflowAction)

            if (nextIds.length) {
              nextIds.forEach((nextId) => {
                if (nextId) {
                  createAction(nextId, newWorkflowAction)
                }
              })
            }
          }
        }
      }

      createAction(firstStepId, trigger)
    } else {
      const trigger = new IncomingCallStore()
      this.addAction(trigger)
      const end = new CallEndsStore({ parentId: trigger.actionStore.id })
      trigger.actionStore.addNextId(end.actionStore.id)
      this.addAction(end)
    }
  }
}
